import Icon from "@expo/vector-icons/MaterialCommunityIcons";
/* -----------------UI--------------- */
import { Card, Divider, Searchbar } from "react-native-paper";

import Box from "@memorang/ui/components/Box";
import { Filters } from "@memorang/ui/components/FilterView";
import ScrollContainer from "@memorang/ui/components/ScrollContainer";

/* -----------------Child components--------------- */
import { ProductsList, type Section } from "./ProductsList";

import { appBarMeta } from "@constants/appbar";
import { events } from "@constants/tracking";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import { formatCount } from "@memorang/helpers/common";
/* -----------------Types--------------- */
import type { ExamDetails } from "@memorang/types";
import { CustomAppBar } from "@memorang/ui/components/CustomAppbar";
import { trackCustomEvent } from "analytics";
import CustomListSubheaderContainer from "components/CustomListSubheaderContainer";
import { useNavigation } from "expo-router";
import { useRef } from "react";
import type { TextInput } from "react-native";

/* -----------------Props--------------- */
type Props = {
	products: Section[];
	handleSwitchExamContext: (data: ExamDetails) => void;
	searchQuery: string;
	handleChangeSearch: (text: string) => void;
	selectedFilters: string[];
	handleSelectFilter: (filter: string) => void;
	filters: string[];
};

/* -----------------Component--------------- */
const ProductsView = (props: Props) => {
	const {
		products,
		handleSwitchExamContext,
		selectedFilters,
		handleSelectFilter,
		filters,
		searchQuery,
		handleChangeSearch,
	} = props;

	const { isMobile } = useDevice();

	const searchRef = useRef<TextInput>(null);

	const theme = useAppTheme();
	const navigation = useNavigation<{ toggleDrawer?: () => void }>();

	const showFilters = filters.length > 1;

	const finalHeaderTitle = isMobile ? "" : appBarMeta.store.title;

	const numItems = products.reduce((acc, section) => {
		return acc + section.data.length;
	}, 0);

	const NumItemsSubheader = () => {
		const numItemsText = `${numItems} ${formatCount("exam", numItems)}`;
		return (
			<CustomListSubheaderContainer enableGutters={false}>
				{numItemsText}
			</CustomListSubheaderContainer>
		);
	};

	const handleClearSearch = () => {
		searchRef.current?.clear();
		handleChangeSearch("");
	};

	const renderSearchbar = () => {
		return (
			<Searchbar
				placeholder={"Search test-prep store..."}
				value={searchQuery}
				ref={searchRef}
				onChangeText={handleChangeSearch}
				clearIcon={() => (
					<Icon name="close" size={24} color={theme.colors.onSurfaceVariant} />
				)}
				style={{
					minWidth: isMobile ? "100%" : 500,
					marginRight: 12,
				}}
			/>
		);
	};

	return (
		<>
			<CustomAppBar
				title={finalHeaderTitle}
				navigation={navigation}
				{...(isMobile
					? { renderCustomCommandBar: renderSearchbar }
					: {
							handleTrackEvent: () => {
								trackCustomEvent({
									eventName: events.commandBarClicked,
									option: "command_bar",
								});
							},
						})}
			/>
			<Box flex={1}>
				<ScrollContainer
					contentContainerStyle={{
						paddingBottom: 100,
						paddingTop: 16,
						paddingHorizontal: isMobile ? 0 : 16,
					}}
					// biome-ignore lint/a11y/useSemanticElements: <explanation>
					role="main"
				>
					<Box gap={16} paddingHorizontal={isMobile ? 16 : 0}>
						{showFilters && (
							<Filters
								filters={filters}
								handleSelectFilter={handleSelectFilter}
								selectedFilters={selectedFilters}
							/>
						)}
						<Box>
							{showFilters && <Divider />}
							<NumItemsSubheader />
						</Box>
					</Box>

					{isMobile ? (
						<ProductsList
							products={products}
							handleSwitchExamContext={handleSwitchExamContext}
							handleClearSearch={handleClearSearch}
							searchQuery={searchQuery}
						/>
					) : (
						<Card
							style={{
								overflow: "hidden",
							}}
						>
							<ProductsList
								products={products}
								handleSwitchExamContext={handleSwitchExamContext}
								handleClearSearch={handleClearSearch}
								searchQuery={searchQuery}
							/>
						</Card>
					)}
				</ScrollContainer>
			</Box>
		</>
	);
};

export default ProductsView;
